import { Children, ComponentProps, ReactElement, isValidElement } from 'react';

import { RiArrowDownSLine as ArrowDropDownIcon } from '@remixicon/react';
import classNames from 'classnames';

import {
  Button,
  Focusable,
  Icon,
  Menu,
  MenuRoot,
  MenuRow,
  Text,
} from '@spotify-internal/encore-web';
import {
  Popover,
  Tooltip,
  TooltipRoot,
} from '@spotify-internal/encore-web/next';

import { useLocalStorage } from '../../hooks/useStorage';

import styles from './SplitButton.module.css';

export interface OptionProps {
  label: string;
  disabled?: boolean;
  onClick?: () => void;
  tooltip?: string;
  description?: string;
  value?: string;
  type?: 'button' | 'submit' | 'reset';
}

export interface SplitButtonOptionProps extends Omit<OptionProps, 'label'> {
  children: string;
}

export interface SplitButtonProps
  extends Omit<
    ComponentProps<typeof Button>,
    'onClick' | 'children' | 'variant'
  > {
  variant?: ComponentProps<typeof Button>['variant'];
  children?:
    | ReactElement<SplitButtonOptionProps>
    | ReactElement<SplitButtonOptionProps>[];
  id: string;
  defaultSelectedIndex?: number;
  ariaLabel?: string;
}

const getStorageKey = (id: string) => `splitbutton-${id}-selected`;

const SplitButtonOption = ({ children }: SplitButtonOptionProps) => {
  return <>{children}</>;
};

const isSplitButtonOptionElement = (
  child: unknown,
): child is ReactElement<SplitButtonOptionProps> => {
  if (!isValidElement(child)) {
    return false;
  }

  if (child.type === SplitButtonOption) {
    return true;
  }

  return (
    typeof child.props === 'object' &&
    child.props !== null &&
    'children' in child.props &&
    typeof child.props.children === 'string' &&
    ('onClick' in child.props ||
      ('type' in child.props && 'value' in child.props))
  );
};

const extractOptionsFromChildren = (
  children:
    | ReactElement<SplitButtonOptionProps>
    | ReactElement<SplitButtonOptionProps>[],
): OptionProps[] => {
  const childArray = Children.toArray(children);
  return childArray.filter(isSplitButtonOptionElement).map((child, index) => ({
    label:
      typeof child.props.children === 'string'
        ? child.props.children
        : `Option ${index + 1}`,
    onClick: child.props.onClick,
    disabled: child.props.disabled,
    tooltip: child.props.tooltip,
    description: child.props.description,
    value: child.props.value,
    type: child.props.type,
  }));
};

const SplitButtonBase = (props: SplitButtonProps) => {
  const {
    children,
    id,
    defaultSelectedIndex = 0,
    disabled = false,
    ariaLabel = 'split button',
    className,
    variant = 'primary',
    type,
    ...sharedButtonProps
  } = props;

  const [selectedIndex, setSelectedIndex] = useLocalStorage(
    getStorageKey(id),
    defaultSelectedIndex,
  );
  const options = children ? extractOptionsFromChildren(children) : [];

  if (options.length === 0) {
    return null;
  }

  const selectedOption = options[selectedIndex] ?? options[0];

  const handleClick = () => {
    if (selectedOption.type === 'submit' || selectedOption.type === 'reset') {
      return;
    }
    if (!selectedOption.disabled) {
      selectedOption.onClick?.();
    }
  };

  const isMainDisabled = Boolean(disabled || selectedOption.disabled);
  const explainsDisabled = Boolean(selectedOption.tooltip) && isMainDisabled;

  const mainButton = (
    <Button
      {...sharedButtonProps}
      variant={variant}
      className={styles.selectedOption}
      onClick={explainsDisabled ? undefined : handleClick}
      disabled={explainsDisabled ? undefined : isMainDisabled}
      isVisuallyDisabled={explainsDisabled || undefined}
      aria-disabled={explainsDisabled || undefined}
      type={explainsDisabled ? 'button' : (selectedOption.type ?? type)}
      value={selectedOption.value}
    >
      {selectedOption.label}
    </Button>
  );

  return (
    <div className={classNames(styles.root, className)} aria-label={ariaLabel}>
      {selectedOption.tooltip ? (
        <TooltipRoot>
          <Focusable>{mainButton}</Focusable>
          <Tooltip placement="top">{selectedOption.tooltip}</Tooltip>
        </TooltipRoot>
      ) : (
        mainButton
      )}
      <MenuRoot>
        <Button
          {...sharedButtonProps}
          variant={variant}
          type="button"
          className={styles.menuToggle}
          aria-label="select action"
          disabled={disabled || options.length <= 1}
        >
          <Icon as={ArrowDropDownIcon} size="small" aria-hidden />
        </Button>
        <Popover placement="bottomEnd" hasNoArrow>
          <Menu aria-label={ariaLabel}>
            {options.map((option, index) => (
              <MenuRow
                key={option.label}
                textValue={option.label}
                isDisabled={option.disabled}
                className={classNames({
                  [styles.menuRowSelected]: index === selectedIndex,
                })}
                onAction={() => setSelectedIndex(index)}
              >
                <span className={styles.menuRowContent}>
                  <Text variant="bodySmall">{option.label}</Text>
                  {option.description && (
                    <Text variant="marginal" semanticColor="textSubdued">
                      {option.description}
                    </Text>
                  )}
                </span>
              </MenuRow>
            ))}
          </Menu>
        </Popover>
      </MenuRoot>
    </div>
  );
};

export default Object.assign(SplitButtonBase, {
  Option: SplitButtonOption,
});
